import { CreditBalance } from "@client/components/credits/credit-balance";
import { Logo, LogoIcon } from "@client/components/logo";
import { ThemeToggle } from "@client/components/navbar/theme-toggle";
import { UserMenu } from "@client/components/navbar/user-menu";
import { Button } from "@client/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@client/components/ui/dropdown-menu";
import { authClient } from "@client/lib/auth-client";
import { MARKETING_SITE } from "@client/lib/site-links";
import { Link, useMatchRoute } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

export function Header() {
	const { data: session } = authClient.useSession();
	const [isOpen, setIsOpen] = useState(false);
	const matchRoute = useMatchRoute();

	// Check if we have a valid session with a user object
	const isAuthenticated = session?.user != null;

	const links = isAuthenticated
		? [
				{ to: "/dashboard", label: "Dashboard" },
				{ to: "/sites", label: "Sites" },
				{ to: "/jobs", label: "Jobs" },
				{ to: "/settings", label: "Settings" },
			]
		: [{ to: MARKETING_SITE, label: "Website" }];

	return (
		<div className="border-border/60 border-b bg-card/80 backdrop-blur-sm">
			<div className="flex flex-row items-center justify-between px-4 py-2">
				{/* Logo / Brand */}
				<div className="flex items-center gap-4">
					<Link
						to={isAuthenticated ? "/dashboard" : "/"}
						className="flex items-center gap-2 text-foreground transition-colors hover:opacity-80"
					>
						{/* Full logo on larger screens */}
						<Logo className="hidden h-7 w-auto sm:block" />
						{/* Icon only on mobile */}
						<LogoIcon className="h-7 w-auto sm:hidden" />
					</Link>

					{/* Desktop Navigation */}
					<nav className="hidden items-center gap-1 md:flex">
						{links.map(({ to, label }) => {
							const isActive = matchRoute({ to, fuzzy: true });
							return (
								<Button
									key={to}
									className={`text-sm ${
										isActive
											? "bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary"
											: "text-muted-foreground hover:text-foreground"
									}`}
									variant="ghost"
									size="sm"
									asChild
								>
									<Link to={to}>{label}</Link>
								</Button>
							);
						})}
					</nav>
				</div>

				{/* Mobile Navigation Menu */}
				<div className="md:hidden">
					<DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
						<DropdownMenuTrigger asChild>
							<Button
								variant="ghost"
								size="sm"
								className="flex items-center gap-1 text-sm"
							>
								Menu
								<ChevronDown
									className={`h-4 w-4 transition-transform duration-200 ${
										isOpen ? "rotate-180" : ""
									}`}
								/>
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent
							align="end"
							className="border border-border/60"
						>
							{links.map(({ to, label }) => (
								<DropdownMenuItem key={to} asChild className="text-sm">
									<Link to={to} className="w-full">
										{label}
									</Link>
								</DropdownMenuItem>
							))}
							<DropdownMenuSeparator />
						</DropdownMenuContent>
					</DropdownMenu>
				</div>

				<div className="flex items-center gap-2">
					{isAuthenticated && <CreditBalance />}
					<ThemeToggle />
					{isAuthenticated ? (
						<UserMenu />
					) : (
						<Button variant="outline" size="sm" asChild>
							<Link to="/">Login</Link>
						</Button>
					)}
				</div>
			</div>
		</div>
	);
}
